import { useState } from 'react'
import {
  VStack,
  Box,
  Text,
  Button,
  Input,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Grid,
  GridItem,
  useToast,
  AccordionIcon
} from '@chakra-ui/react'
import { Select, Select as MultiSelect } from 'chakra-react-select'
import { useDTStore, DTInstanceType } from '../../stores/dtStore'

export const DigitalTwinsConfig = () => {
  const [newInstanceName, setNewInstanceName] = useState('')
  const [newAggregateName, setNewAggregateName] = useState('')
  const [selectedDataSource, setSelectedDataSource] = useState<string | null>(null)
  const [selectedInstances, setSelectedInstances] = useState<string[]>([])
  const [selectedInstanceType, setSelectedInstanceType] = useState<DTInstanceType>('sensor')
  const { digitalTwins, addDigitalTwin, getDataSources, getInstances } = useDTStore()
  const toast = useToast()

  const instanceTypeOptions = [
    { value: 'sensor' as DTInstanceType, label: 'Sensor' },
    { value: 'equipment' as DTInstanceType, label: 'Equipment' },
    { value: 'system' as DTInstanceType, label: 'System' },
    { value: 'process' as DTInstanceType, label: 'Process' }
  ]

  const handleCreateInstance = () => {
    if (!newInstanceName || !selectedDataSource || !selectedInstanceType) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        status: 'error',
        duration: 3000,
      })
      return
    }

    addDigitalTwin({
      name: newInstanceName,
      category: 'instance',
      type: selectedInstanceType,
      dataSource: selectedDataSource
    })

    setNewInstanceName('')
    setSelectedDataSource(null)
    
    toast({
      title: 'Success',
      description: 'Digital Twin instance created',
      status: 'success',
      duration: 3000,
    })
  }

  const handleCreateAggregate = () => {
    if (!newAggregateName || selectedInstances.length === 0) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields and select at least one instance',
        status: 'error',
        duration: 3000,
      })
      return
    }

    addDigitalTwin({
      name: newAggregateName,
      category: 'aggregate',
      children: selectedInstances
    })

    setNewAggregateName('')
    setSelectedInstances([])
    
    toast({
      title: 'Success',
      description: 'Digital Twin aggregate created',
      status: 'success',
      duration: 3000,
    })
  }

  const dataSources = getDataSources()
  const instances = getInstances()

  return (
    <Accordion allowMultiple width="100%">
      <AccordionItem>
        <AccordionButton>
          <Text fontWeight="bold" flex="1">Create DT Instance</Text>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text mb={2}>Instance Name</Text>
              <Input
                value={newInstanceName}
                onChange={(e) => setNewInstanceName(e.target.value)}
                placeholder="Enter instance name"
              />
            </Box>
            <Box>
              <Text mb={2}>Instance Type</Text>
              <Select
                options={instanceTypeOptions}
                value={instanceTypeOptions.find(opt => opt.value === selectedInstanceType)}
                onChange={(option) => setSelectedInstanceType(option?.value || 'sensor')}
                placeholder="Select instance type"
              />
            </Box>
            <Box>
              <Text mb={2}>Data Source</Text>
              <Select
                options={dataSources.map(ds => ({ value: ds, label: ds }))}
                value={selectedDataSource ? { value: selectedDataSource, label: selectedDataSource } : null}
                onChange={(option) => setSelectedDataSource(option?.value || null)}
                placeholder="Select data source"
              />
            </Box>
            <Button colorScheme="blue" onClick={handleCreateInstance}>
              Create Instance
            </Button>
          </VStack>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem>
        <AccordionButton>
          <Text fontWeight="bold" flex="1">Create DT Aggregate</Text>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text mb={2}>Aggregate Name</Text>
              <Input
                value={newAggregateName}
                onChange={(e) => setNewAggregateName(e.target.value)}
                placeholder="Enter aggregate name"
              />
            </Box>
            <Box>
              <Text mb={2}>Select Instances</Text>
              <MultiSelect
                isMulti
                options={instances.map(inst => ({ value: inst.id, label: inst.name }))}
                onChange={(options) => setSelectedInstances(options.map(opt => opt.value))}
                placeholder="Select instances"
              />
            </Box>
            <Button colorScheme="blue" onClick={handleCreateAggregate}>
              Create Aggregate
            </Button>
          </VStack>
        </AccordionPanel>
      </AccordionItem>

      <AccordionItem>
        <AccordionButton>
          <Text fontWeight="bold" flex="1">View Digital Twins</Text>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel>
          <Grid templateColumns="1fr 1fr" gap={6}>
            <GridItem>
              <Text fontWeight="bold" mb={4}>Aggregates</Text>
              {digitalTwins
                .filter(dt => dt.category === 'aggregate')
                .map(dt => (
                  <Box key={dt.id} p={3} bg="gray.100" mb={2} borderRadius="md">
                    {dt.name}
                  </Box>
                ))}
            </GridItem>
            <GridItem>
              <Text fontWeight="bold" mb={4}>Instances</Text>
              {digitalTwins
                .filter(dt => dt.category === 'instance')
                .map(dt => (
                  <Box key={dt.id} p={3} bg="gray.100" mb={2} borderRadius="md">
                    {dt.name} ({dt.type}) - {dt.dataSource}
                  </Box>
                ))}
            </GridItem>
          </Grid>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  )
} 